import { useCallback } from "react";
import { useAppDispatch } from "../../common/store";
import { sendMessageLogSubmission } from "../../common/store/chat/thunks/send-message-log";
import VurbalizeEvents from "../../common/types/dto/chat/vurbalize-events";
import getMessageByVurablizeEvent from "../utils/get-message-by-vurablize-event";
import { PostHighlightAndRedirectRequestDto } from "../../common/types/dto/chat/post-highlight-and-redirect-request-dto";
import { PostHighlightAndRedirectResponseDto } from "../../common/types/dto/chat/post-highlight-and-redirect-response-dto";
// import { PostHighlightAndRedirectRequestDto } from "../../common/store/chat/thunks/hyper-link-click";

const voaNavigationLocalStorageKey = 'voa_navigation';

export const useNavigateAndHighlight = () => {
  const dispatch = useAppDispatch();

  const navigateTo = useCallback((targetUrl: string) => {
    window.location.href = targetUrl;
  }, [window.location.href]);

  const findPageEntries = useCallback((targetValue: string) => {
    const excludedElements = [
      'VurbalizeCodeExt',
      '__NEXT_DATA__',
      'utag_data',
      'utag_script',
      'vwo'
    ];
    const excludedNodes = excludedElements.map((id) => document.getElementById(id));

    const targetValuesIterator = document.createNodeIterator(document.body, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        try {
          if ((excludedNodes.some((excludedNode) => excludedNode ? excludedNode.contains(node) : false))) {
            return NodeFilter.FILTER_REJECT;
          }

          return (node.textContent?.includes(targetValue) || node.textContent === targetValue )
            ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
        } catch (e) {
          return NodeFilter.FILTER_REJECT;
        }
      }
    });
    
    let currentNode: Node | null = targetValuesIterator.nextNode();

    const out: Array<Node> = [];

    while (currentNode) {
      out.push(currentNode);

      currentNode = targetValuesIterator.nextNode();
    }

    return out;
  }, []);

  const highlightEntry = useCallback((node: Node | null, targetString: string) => {
    if (!node) {
      return null;
    }

    const textContent = node.textContent!
  
    const matchIndex = textContent.indexOf(targetString);
  
    // Create a wrapper span with highlight style
    const highlightSpan = document.createElement('span');
    highlightSpan.style.backgroundColor = 'yellow'; // Highlight style
    highlightSpan.textContent = targetString;
  
    // Split the text node into before, match, and after parts
    const beforeText = textContent.slice(0, matchIndex);
    const afterText = textContent.slice(matchIndex + targetString.length);
  
    // Create text nodes for the before and after parts
    const beforeNode = document.createTextNode(beforeText);
    const afterNode = document.createTextNode(afterText);
  
    // Replace the original text node with the new nodes
    const parentNode = node.parentNode;
    if (parentNode) {
      parentNode.insertBefore(beforeNode, node);
      parentNode.insertBefore(highlightSpan, node);
      parentNode.insertBefore(afterNode, node);
      parentNode.removeChild(node); // Remove the original text node
    }

    return highlightSpan;
  }, []);

  const highlightEntries = useCallback((nodes: Array<Node>, targetString: string) => {
    return nodes.map((node) => {
      highlightEntry(node, targetString);
    });
  }, [highlightEntry]);

  const highlightAndScroll = useCallback((scrollDelay: number) => {
    const highlightElementsStorage = localStorage.getItem(voaNavigationLocalStorageKey);
    localStorage.removeItem(voaNavigationLocalStorageKey);

    if (!highlightElementsStorage) {
      return;
    }
    const highlightElements = JSON.parse(highlightElementsStorage) as PostHighlightAndRedirectResponseDto;

    if (highlightElements.hero_page_visited) {
      dispatch(sendMessageLogSubmission({
        data: {
          message: getMessageByVurablizeEvent(VurbalizeEvents.HERO_PAGE_VISITED),
          event_name: VurbalizeEvents.HERO_PAGE_VISITED,
          event_data: {
            timestamp: Date.now(),
            pageUrl: window.location.href,
          },
        }
      }));
    }

    const highlightedNodes = highlightElements.lines_to_highlight.map(({ text, occurence }) => (
      highlightEntry(
        findPageEntries(text)[occurence - 1] || null,
        text
      )
    ));

    const firstHighlightedElement = highlightedNodes[0];

    if (!firstHighlightedElement) {
      return;
    }

    // without delay not working sometime
    setTimeout(() => {
      firstHighlightedElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }, scrollDelay);
  }, [findPageEntries]);

  const triggerNavigation = useCallback((data: PostHighlightAndRedirectResponseDto) => {
    const { url } = data;

    localStorage.setItem(voaNavigationLocalStorageKey, JSON.stringify({...data, url }));

    if (window.location.href === url) {
      highlightAndScroll(1000);
      // here should be scroll to and highlight
    } else {
      navigateTo(url);
    }
  }, [navigateTo]);

  return {
    triggerNavigation,
    findPageEntries,
    highlightEntries,
    highlightAndScroll
  };
};
